import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  MarketPrice,
  PutAdminV1StoresStoreIdMarketsMarketIdPricesBody,
} from '@nucleus-commerce/api-client/admin';

import { marketKeys } from '@/features/markets';

import { storeCatalogApi } from '../api/store-catalog-api';

import { storeCatalogKeys } from './store-catalog-keys';

/** Every fixed price of one product in one market; a product has few variants, so all pages are read. */
export function marketPricesQueryOptions(storeId: string, marketId: string, productId: string) {
  return queryOptions({
    queryKey: storeCatalogKeys.marketPrices(storeId, marketId, productId),
    queryFn: async () => {
      const items: MarketPrice[] = [];
      let cursor: string | undefined;
      for (;;) {
        const page = await storeCatalogApi.marketPricesPage(storeId, marketId, {
          productId,
          ...(cursor ? { cursor } : {}),
        });
        items.push(...page.items);
        if (!page.nextCursor) return { items, currency: page.currency };
        cursor = page.nextCursor;
      }
    },
  });
}

export function useMarketPrices(storeId: string, marketId: string | null, productId: string) {
  return useQuery({
    ...marketPricesQueryOptions(storeId, marketId ?? '', productId),
    enabled: marketId !== null,
  });
}

/**
 * The store price grid shows what the default market charges, and the first fixed price gives the
 * market a price list, so both are refetched along with the market's own prices.
 */
function useInvalidateMarketPricing(storeId: string, storeProductId: string) {
  const queryClient = useQueryClient();
  return (marketId: string, productId: string) =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: storeCatalogKeys.marketPrices(storeId, marketId, productId),
      }),
      queryClient.invalidateQueries({
        queryKey: storeCatalogKeys.pricing(storeId, storeProductId),
      }),
      queryClient.invalidateQueries({ queryKey: marketKeys.list(storeId) }),
    ]);
}

export function useSetMarketPrices(storeId: string, storeProductId: string, productId: string) {
  const invalidate = useInvalidateMarketPricing(storeId, storeProductId);
  return useMutation({
    mutationFn: ({
      marketId,
      body,
    }: {
      marketId: string;
      body: PutAdminV1StoresStoreIdMarketsMarketIdPricesBody;
    }) => storeCatalogApi.setMarketPrices(storeId, marketId, body),
    meta: { successMessage: 'storeCatalog:marketPrices.toasts.saved', silentError: true },
    onSuccess: (_rows, { marketId }) => invalidate(marketId, productId),
  });
}

export function useRemoveMarketPrice(storeId: string, storeProductId: string, productId: string) {
  const invalidate = useInvalidateMarketPricing(storeId, storeProductId);
  return useMutation({
    mutationFn: ({ marketId, variantId }: { marketId: string; variantId: string }) =>
      storeCatalogApi.removeMarketPrice(storeId, marketId, variantId),
    meta: { successMessage: 'storeCatalog:marketPrices.toasts.removed', silentError: true },
    onSuccess: (_void, { marketId }) => invalidate(marketId, productId),
  });
}
